/**
 * Analytics Component
 * 
 * Marketing analytics and application funnel visualization
 * Helps admins understand user drop-off points and conversion rates
 */
import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown, 
  Users, 
  ArrowRight,
  Filter,
  Calendar,
  Download,
  DollarSign,
  ShoppingCart,
  PieChart
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../common';
import { H3, Text } from '../../../common/Typography';
import { Button } from '../../../common';
import { useAnalyticsQuery } from '../../../hooks/queries/admin';

const Analytics = () => {
  const [applications, setApplications] = useState([]);
  const [summary, setSummary] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [aiTokenUsage, setAiTokenUsage] = useState(null);
  const [businessMetrics, setBusinessMetrics] = useState(null);
  const [contactCapturedAppIds, setContactCapturedAppIds] = useState([]);
  const [funnelData, setFunnelData] = useState([]);
  const [quoteLeadCount, setQuoteLeadCount] = useState(0);
  const [timeRange, setTimeRange] = useState('all'); // 'week', 'month', 'year', 'all'

  const { data: stats, isLoading, error, refetch } = useAnalyticsQuery();

  const formatDuration = (ms) => {
    if (!ms) return '-';
    const minutes = Math.floor(ms / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m`;
    return '< 1m';
  };

  useEffect(() => {
    if (stats) {
      const apps = Array.isArray(stats.applications) ? stats.applications : [];
      // Only show marketing wizard applications in the funnel
      const wizardApps = apps.filter(app => app.source === 'wizard');
      setApplications(wizardApps);
      setSummary(stats.summary);
      setAiUsage(stats.aiUsage);
      setAiTokenUsage(stats.aiTokenUsage);
      setBusinessMetrics(stats.businessMetrics);
      setContactCapturedAppIds(stats.contactCapturedAppIds || []);
      
      // Compute funnel data from wizard applications only
      computeFunnel(wizardApps, stats);
    }
  }, [stats]);

  useEffect(() => {
    if (applications.length > 0) {
      computeFunnel(applications, stats);
    }
  }, [timeRange, applications]);

  const computeFunnel = (apps, data) => {
    // Filter by time range if needed
    let filteredApps = apps;
    const now = new Date();
    
    if (timeRange === 'week') {
      const weekAgo = new Date(now.setDate(now.getDate() - 7));
      filteredApps = apps.filter(app => new Date(app.createdAt) >= weekAgo);
    } else if (timeRange === 'month') {
      const monthAgo = new Date(now.setMonth(now.getMonth() - 1));
      filteredApps = apps.filter(app => new Date(app.createdAt) >= monthAgo);
    } else if (timeRange === 'year') {
      const yearAgo = new Date(now.setFullYear(now.getFullYear() - 1));
      filteredApps = apps.filter(app => new Date(app.createdAt) >= yearAgo);
    }

    // Quote-first funnel: quote-stage leads (report requested / quote sent, not
    // yet paid) are excluded from the payment-conversion funnel so they don't
    // crater the displayed conversion rate. They are surfaced as a separate count.
    const isQuoteLead = (app) =>
      (app.status === 'quote_requested' || app.status === 'quote_sent') &&
      app.paymentStatus !== 'paid';
    setQuoteLeadCount(filteredApps.filter(isQuoteLead).length);
    filteredApps = filteredApps.filter(app => !isQuoteLead(app));

    // Define Funnel Steps (Marketing Wizard flow)
    const steps = [
      { id: 'start', label: 'Landed on Wizard', count: 0, totalDuration: 0, durationCount: 0 },
      { id: 'trademark', label: 'Trademark Entered', count: 0, totalDuration: 0, durationCount: 0 },
      { id: 'business', label: 'Business Info', count: 0, totalDuration: 0, durationCount: 0 },
      { id: 'analysis', label: 'AI Analysis Complete', count: 0, totalDuration: 0, durationCount: 0 },
      { id: 'contact', label: 'Contact Captured', count: 0, totalDuration: 0, durationCount: 0 },
      { id: 'countries', label: 'Countries & Classes', count: 0, totalDuration: 0, durationCount: 0 },
      { id: 'payment', label: 'Payment', count: 0, totalDuration: 0, durationCount: 0 },
      { id: 'completed', label: 'Filing Complete', count: 0, totalDuration: 0, durationCount: 0 }
    ];

    // Set of applicationIds with captured contacts (from backend)
    const contactAppIds = new Set(data?.contactCapturedAppIds || []);

    filteredApps.forEach(app => {
      // Step 1: Started (All apps)
      steps[0].count++;

      // Step 2: Trademark Info (has trademarkId or name)
      if (app.trademarkId || (app.trademarkName && app.trademarkName !== 'Unnamed Trademark')) {
        steps[1].count++;
      } else {
        return; // Drop off
      }

      // Step 3: Business Info
      if (app.businessInfoId || app.businessName) {
        steps[2].count++;
      } else {
        return;
      }

      // Step 4: AI Analysis
      if (app.analysisId) {
        steps[3].count++;
      } else {
        return;
      }

      // Step 5: Contact Captured
      if (contactAppIds.has(app.id)) {
        steps[4].count++;
      } else {
        return;
      }

      // Step 6: Country Selection (Pricing)
      if (app.pricingId) {
        steps[5].count++;
      } else {
        return;
      }

      // Step 7: Payment
      if (app.paymentId || app.paymentStatus === 'paid' || app.status === 'completed' || app.status === 'registered') {
        steps[6].count++;
      } else {
        return;
      }

      // Step 8: Completed
      if (app.status === 'completed' || app.status === 'registered') {
        steps[7].count++;
      }

      // Accumulate Durations
      if (app.stepDurations) {
          if (app.stepDurations.startToTrademark) {
              steps[1].totalDuration += app.stepDurations.startToTrademark;
              steps[1].durationCount++;
          }
          if (app.stepDurations.trademarkToBusiness) {
              steps[2].totalDuration += app.stepDurations.trademarkToBusiness;
              steps[2].durationCount++;
          }
          if (app.stepDurations.businessToAnalysis) {
              steps[3].totalDuration += app.stepDurations.businessToAnalysis;
              steps[3].durationCount++;
          }
          if (app.stepDurations.totalToCompletion) {
              steps[6].totalDuration += app.stepDurations.totalToCompletion;
              steps[6].durationCount++;
          }
      }
    });

    // Calculate conversion rates and averages
    const funnelWithRates = steps.map((step, index) => {
      const prevCount = index > 0 ? steps[index - 1].count : step.count;
      const conversionRate = prevCount > 0 ? ((step.count / prevCount) * 100).toFixed(1) : 0;
      const dropOffRate = (100 - conversionRate).toFixed(1);
      const totalConversion = steps[0].count > 0 ? ((step.count / steps[0].count) * 100).toFixed(1) : 0;
      const avgDuration = step.durationCount > 0 ? step.totalDuration / step.durationCount : 0;
      
      return {
        ...step,
        conversionRate,
        dropOffRate,
        totalConversion,
        avgDurationFormatted: avgDuration > 0 ? formatDuration(avgDuration) : null
      };
    });

    setFunnelData(funnelWithRates);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse h-64 bg-gray-200 rounded-xl"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="animate-pulse h-48 bg-gray-200 rounded-xl"></div>
          <div className="animate-pulse h-48 bg-gray-200 rounded-xl"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <p className="text-red-500 mb-4">{error.message || 'Failed to load analytics data'}</p>
          <Button onClick={() => refetch()}>Retry</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Filters */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <H3>Marketing Analytics</H3>
          <Text className="text-gray-500">Track application progress and conversion funnel</Text>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="pl-9 pr-4 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00796b]"
            >
              <option value="all">All Time</option>
              <option value="year">Past Year</option>
              <option value="month">Past Month</option>
              <option value="week">Past Week</option>
            </select>
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          </div>
          <Button variant="outline" size="sm" className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Business Overview */}
      {businessMetrics && (
        <div className="space-y-6">
            <H3 className="text-gray-900">Business Performance</H3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-green-50 rounded-lg">
                                <DollarSign className="h-5 w-5 text-green-600" />
                            </div>
                            <p className="font-medium text-gray-600">Total Revenue</p>
                        </div>
                        <H3>${businessMetrics.revenue.total.toFixed(2)}</H3>
                        <p className="text-sm text-gray-500 mt-1">{businessMetrics.revenue.count} Orders</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-blue-50 rounded-lg">
                                <ShoppingCart className="h-5 w-5 text-blue-600" />
                            </div>
                            <p className="font-medium text-gray-600">Avg Order Value</p>
                        </div>
                        <H3>${businessMetrics.revenue.avgOrderValue.toFixed(2)}</H3>
                    </CardContent>
                </Card>
                 <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-purple-50 rounded-lg">
                                <PieChart className="h-5 w-5 text-purple-600" />
                            </div>
                            <p className="font-medium text-gray-600">Top Market</p>
                        </div>
                        <H3>{Object.entries(businessMetrics.insights.countries).sort((a,b) => b[1]-a[1])[0]?.[0] || 'N/A'}</H3>
                         <p className="text-sm text-gray-500 mt-1">Most filings</p>
                    </CardContent>
                </Card>
            </div>
        </div>
      )}

      {/* Main Funnel Chart */}
      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-[#00796b]" />
            Application Funnel
          </CardTitle>
          {quoteLeadCount > 0 && (
            <Text className="text-sm text-gray-500 mt-1">
              {quoteLeadCount} quote-stage lead{quoteLeadCount === 1 ? '' : 's'} (report requested / quote sent, unpaid) excluded from conversion totals
            </Text>
          )}
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {funnelData.map((step, index) => {
              // Calculate width relative to start count (max width)
              const widthPercentage = funnelData[0].count > 0 ? (step.count / funnelData[0].count) * 100 : 0;
              
              return (
                <div key={step.id} className="relative">
                  <div className="flex justify-between items-end mb-1">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </span>
                      <span className="font-medium text-gray-700">{step.label}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-gray-900">{step.count}</span>
                      <span className="text-xs text-gray-500 ml-1">users</span>
                    </div>
                  </div>
                  
                  {/* Bar */}
                  <div className="h-4 w-full bg-gray-100 rounded-full overflow-hidden flex">
                    <div 
                      className={`h-full rounded-full transition-all duration-1000 ease-out ${
                        index === 0 ? 'bg-[#004d40]' : 
                        index === funnelData.length - 1 ? 'bg-green-500' : 'bg-[#00796b]'
                      }`}
                      style={{ width: `${Math.max(widthPercentage, 1)}%` }}
                    />
                  </div>
                  
                  {/* Stats overlay */}
                  <div className="mt-1 flex justify-between text-xs">
                    <div className="flex items-center gap-3">
                      {index > 0 && (
                        <div className="flex items-center gap-1 text-green-600">
                          <TrendingUp className="h-3 w-3" />
                          <span>{step.conversionRate}% conversion</span>
                        </div>
                      )}
                      
                      {step.avgDurationFormatted && (
                        <div className="flex items-center gap-1 text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded">
                          <span className="text-[10px]">⏱️</span>
                          <span>{step.avgDurationFormatted}</span>
                        </div>
                      )}
                    </div>

                    {index > 0 && (
                      <div className="flex items-center gap-1 text-red-500">
                        <TrendingDown className="h-3 w-3" />
                        <span>{step.dropOffRate}% drop-off</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <p className="font-medium text-gray-600">Active Users</p>
            </div>
            <H3>{summary?.totalUsers || 0}</H3>
            <p className="text-sm text-gray-500 mt-1">Engaged users (Active or Contact)</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-50 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <p className="font-medium text-gray-600">Completion Rate</p>
            </div>
            <H3>{funnelData[funnelData.length - 1]?.totalConversion || 0}%</H3>
            <p className="text-sm text-gray-500 mt-1">Start to Finish conversion</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-amber-50 rounded-lg">
                <Filter className="h-5 w-5 text-amber-600" />
              </div>
              <p className="font-medium text-gray-600">Highest Drop-off</p>
            </div>
            {(() => {
              // Find step with highest drop-off rate
              let maxDropStep = null;
              let maxDropRate = -1;
              funnelData.slice(1).forEach(step => {
                const dropRate = parseFloat(step.dropOffRate);
                if (dropRate > maxDropRate) {
                  maxDropRate = dropRate;
                  maxDropStep = step;
                }
              });
              
              return (
                <>
                  <H3>{maxDropStep ? maxDropStep.label : 'N/A'}</H3>
                  <p className="text-sm text-red-500 mt-1">
                    {maxDropRate > 0 ? `${maxDropRate}% users leave here` : 'No drop-off data'}
                  </p>
                </>
              );
            })()}
          </CardContent>
        </Card>
      </div>

      {/* AI Usage Stats */}
      {aiUsage && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="p-1.5 bg-purple-100 rounded-lg">
                <Users className="h-4 w-4 text-purple-600" />
              </div>
              AI Analysis Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-xl">
                <H3>{aiUsage.totalAnalyses}</H3>
                <Text className="text-sm text-gray-500">Total Analyses</Text>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-xl">
                <H3>{aiUsage.riskBreakdown?.low || 0}</H3>
                <Text className="text-sm text-green-600 font-medium">Low Risk</Text>
              </div>
              <div className="text-center p-4 bg-amber-50 rounded-xl">
                <H3>{aiUsage.riskBreakdown?.medium || 0}</H3>
                <Text className="text-sm text-amber-600 font-medium">Medium Risk</Text>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-xl">
                <H3>{aiUsage.riskBreakdown?.high || 0}</H3>
                <Text className="text-sm text-red-600 font-medium">High Risk</Text>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Token Usage */}
      {aiTokenUsage && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="p-1.5 bg-indigo-100 rounded-lg">
                <BarChart3 className="h-4 w-4 text-indigo-600" />
              </div>
              OpenAI Token Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-xl">
                <H3>{aiTokenUsage.totalRequests || 0}</H3>
                <Text className="text-sm text-gray-500">Total API Calls</Text>
              </div>
              <div className="text-center p-4 bg-indigo-50 rounded-xl">
                <H3>{(aiTokenUsage.totalTokens || 0).toLocaleString()}</H3>
                <Text className="text-sm text-indigo-600 font-medium">Total Tokens</Text>
              </div>
              {Object.entries(aiTokenUsage.byType || {}).map(([type, data]) => (
                <div key={type} className="text-center p-4 bg-blue-50 rounded-xl capitalize">
                  <H3>{data.requests}</H3>
                  <Text className="text-sm text-blue-600 font-medium">{type} Requests</Text>
                </div>
              ))}
            </div>
            
            {aiTokenUsage.byModel && Object.keys(aiTokenUsage.byModel).length > 0 && (
              <div className="mt-6">
                 <h4 className="text-sm font-medium text-gray-700 mb-3">Model Breakdown</h4>
                 <div className="overflow-x-auto">
                   <table className="min-w-full text-sm">
                     <thead className="bg-gray-50">
                       <tr>
                         <th className="px-4 py-2 text-left text-gray-500 font-medium rounded-l-lg">Model</th>
                         <th className="px-4 py-2 text-right text-gray-500 font-medium">Requests</th>
                         <th className="px-4 py-2 text-right text-gray-500 font-medium rounded-r-lg">Tokens Used</th>
                       </tr>
                     </thead>
                     <tbody>
                       {Object.entries(aiTokenUsage.byModel).map(([model, data]) => (
                         <tr key={model} className="border-b border-gray-100 last:border-0">
                           <td className="px-4 py-3 font-medium text-gray-900">{model}</td>
                           <td className="px-4 py-3 text-right text-gray-600">{data.requests}</td>
                           <td className="px-4 py-3 text-right text-gray-600">{data.tokens.toLocaleString()}</td>
                         </tr>
                       ))}
                     </tbody>
                   </table>
                 </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Analytics;
